<template>
  <footer aria-label="Footer" class="z-9 relative print:hidden">
    <!-- Sustenability -->
    <Susteinability />
    <!-- Top footer -->
    <div v-if="!compact" class="bg-[color:var(--color-footer-top-bg)]">
      <div class="mx-auto grid grid-cols-1 items-start gap-y-5 px-4 pb-5 pt-8 md:grid-cols-3 md:p-8 lg:w-[994px]">
        <div
          class="grid grid-cols-1 gap-5 justify-self-center overflow-hidden text-center md:row-span-3 md:max-w-[78%] md:justify-self-start md:text-left"
        >
          <FooterSales :sales-phone="footerBlock?.salesPhone" :sales-email="footerBlock?.salesEmail" />
          <FooterSupport
            :technical-phone="footerBlock?.technicalPhone"
            :technical-email="footerBlock?.technicalEmail"
          />
        </div>

        <div
          class="grid grid-cols-1 gap-5 justify-self-center text-center md:row-span-3 md:justify-self-start md:text-left lg:-ml-14"
        >
          <FooterHours :hours-line1="footerBlock?.hoursLine1" :hours-line2="footerBlock?.hoursLine2" />

          <div class="grid gap-y-2 md:justify-start">
            <div
              v-if="(footerBlock?.linkedPages?.length ?? 0) > 0 || (footerBlock?.companyLinks?.length ?? 0) > 0"
              class="text-sm font-bold text-[color:var(--color-footer-top-text)] md:block"
            >
              {{ $t("shared.layout.footer.company") }}
            </div>
            <div class="flex flex-wrap justify-center gap-x-4 gap-y-2 md:grid md:justify-start">
              <template v-if="footerBlock?.linkedPages">
                <FooterLink
                  v-for="page in footerBlock.linkedPages"
                  :key="page.href"
                  :title="page.text"
                  :href="page.href"
                  :target="page.target"
                  class="md:order-none md:w-auto"
                >
                </FooterLink>
              </template>
              <FooterCompanyLinks v-if="footerBlock?.companyLinks?.length" :company-links="footerBlock.companyLinks" />
              <template v-if="footerBlock?.additionalLink">
                <a
                  class="footer-link order-last w-full text-sm font-medium text-[color:var(--color-footer-top-link)] hover:text-[color:var(--color-footer-top-link-hover)]"
                  :href="footerBlock?.additionalLink.link"
                  :class="{
                    'w-full pt-2': currencyCode === 'GBP',
                  }"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <slot>{{ footerBlock?.additionalLink.displayName }}</slot>
                </a>
              </template>
            </div>
          </div>
        </div>

        <div
          class="-order-1 grid w-full grid-cols-2 items-center gap-3 justify-self-center md:order-none md:ml-0 md:grid"
        >
          <CurrencySelector
            v-if="$context.availableCurrencies && $context.availableCurrencies.length > 1"
            class="h-full"
          />
          <LanguageSelector
            v-if="$context.availableLanguages && $context.availableLanguages.length > 1"
            :page-overlay-locales="pageOverlayLocales"
          />
        </div>

        <div
          class="my-auto grid w-full grid-cols-1 justify-self-center border-t border-t-[var(--color-neutral-a4)] pt-3 md:ml-0 md:border-0 md:pt-0"
        >
          <FooterSocials :social-media-links="footerBlock?.socialMediaLinks" />
        </div>

        <div class="mt-auto grid grid-cols-1 items-center gap-y-2 justify-self-center text-center md:ml-0">
          <span class="text-xs font-medium text-[color:var(--color-footer-top-text)]">
            {{ $t("shared.layout.footer.copyright", { currentYear: new Date().getFullYear() }) }}
          </span>
          <div :class="['flex', isChinaDomain() && chinaDomainIdentifier && isMobile ? 'gap-x-4' : 'flex-col gap-y-2']">
            <a
              v-if="isChinaDomain() && chinaDomainIdentifier"
              :href="chinaDomainIdentifier.link"
              class="footer-link text-sm font-medium text-[color:var(--color-footer-top-link)] hover:text-[color:var(--color-footer-top-link-hover)]"
              >{{ chinaDomainIdentifier.displayName }}</a
            >

            <a
              href="#"
              class="footer-link text-sm !font-medium text-[color:var(--color-footer-top-link)] hover:text-[color:var(--color-footer-top-link-hover)]"
              @click.prevent="openCookieSettings"
            >
              {{ $t("common.buttons.manage_cookie_preferences") }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </footer>
  <!--LivePerson chat will be injected here-->
  <div
    ref="chatLauncher"
    :class="[
      'chat-launcher',
      chatPosition,
      { 'chat-launcher--minimized': isChatMinimized, 'chat-launcher--loading': !isChatLauncherReady },
      showChatFooter && !isChatWindowActive && agentsAvailable ? '' : 'hidden',
    ]"
  >
    <button
      v-if="!isMobile && !isChatMinimized"
      type="button"
      class="chat-launcher__minimize"
      :aria-label="$t('shared.layout.footer.minimize_chat', 'Minimize chat')"
      :title="$t('shared.layout.footer.minimize_chat', 'Minimize chat')"
      @click.stop="toggleChatLauncher"
    >
      <span class="chat-launcher__minimize-icon">
        <VcIcon class="text-[--color-neutral-a1]" name="minus-alt" :size="12" />
      </span>
    </button>
    <div v-else-if="!isMobile && isChatMinimized" class="chat-launcher__sidebar">
      <button
        ref="restoreChatButton"
        type="button"
        class="chat-launcher__restore"
        :aria-label="$t('shared.layout.footer.restore_chat', 'Restore chat')"
        :title="$t('shared.layout.footer.restore_chat', 'Restore chat')"
        @click.stop="toggleChatLauncher"
      >
        <VcIcon name="chevron-left" class="shrink-0 stroke-[white] stroke-2 text-[--color-neutral-a1]" :size="18" />
      </button>
      <button
        type="button"
        class="chat-launcher__open"
        :aria-label="$t('shared.layout.footer.chat_with_us', 'Chat with Us')"
        :title="$t('shared.layout.footer.chat_with_us', 'Chat with Us')"
        @click.stop="openLivePersonChat"
      >
        <VcIcon :class="chatLauncherMode" name="chat-with-us" :size="28" />
        <span class="chat-launcher__restore-label" aria-hidden="true">{{
          $t("shared.layout.footer.chat_with_us", "Chat with Us")
        }}</span>
      </button>
    </div>
    <div
      v-show="!isChatMinimized"
      id="LP_DIV_1750955724927"
      :class="[
        'bubble-container',
        isAtBottom ? 'stay' : isScrollingUp ? 'slideDown' : 'slideUp',
        showChatFooter && !isChatWindowActive && agentsAvailable ? '' : 'hidden',
      ]"
    >
      <!-- This is for local testing DO NOT REMOVE -->
      <!-- <button
        ref="expandedChatButton"
        type="button"
        class="vc-icon-with-label vc-icon-with-label--bottom chat-widget__icon"
        :aria-label="$t('shared.layout.footer.chat_with_us', 'Chat with Us')"
        @click="openLivePersonChat"
      >
        <svg class="vc-icon available-mode shrink-0">
          <use href="/static/thorlabs/icons/basic/chat-with-us.svg?v=202663#icon"></use>
        </svg>
        <span class="vc-icon-with-label__text">{{ $t("shared.layout.footer.chat_with_us", "Chat with Us") }}</span>
      </button>-->
    </div>
  </div>
  <!-- Bottom footer -->
  <!-- This is for local testing DO NOT REMOVE -->
  <!-- <div class="flex justify-center bg-[color:var(--color-footer-bottom-bg)] py-[10px] print:flex-row">
    <VcImage :src="$cfg.logo_trusted_image" alt="" role="presentation" class="h-[40px]" lazy />
  </div> -->
  <div class="hidden">{{ appVersion }}</div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { computed, nextTick, watch, ref, onBeforeUnmount, onMounted } from "vue";
import { useCurrency } from "@/core/composables";
import { useFooterBlock } from "@/core/composables/useFooterBlock";
import { useOverlayLocales } from "@/core/composables/useOverlayLocales";
import { useSlugInfo } from "@/shared/common";
import {
  useLivePerson,
  showChatFooter,
  isChatWindowActive,
  agentsAvailable,
  initChatWindowObserver,
  cleanupChatWindowObserver,
  initCoBrowseDetection,
  cleanupCoBrowseDetection,
  startAgentAvailabilityPolling,
  stopAgentAvailabilityPolling,
} from "@/shared/live-person";
import { eventBus } from "@/shared/thorlabs/shared";
import { Susteinability } from "@/shared/thorlabs/support-documents";
import pkg from "../../../../../package.json";
import FooterCompanyLinks from "./_internal/footer-company-links.vue";
import FooterHours from "./_internal/footer-hours.vue";
import FooterLink from "./_internal/footer-link.vue";
import FooterSales from "./_internal/footer-sales.vue";
import FooterSocials from "./_internal/footer-socials.vue";
import FooterSupport from "./_internal/footer-support.vue";
import type { IPage } from "@/shared/thorlabs/shared";
import CurrencySelector from "@/shared/layout/components/currency-selector/currency-selector.vue";
import LanguageSelector from "@/shared/layout/components/language-selector/language-selector.vue";

defineProps<IProps>();

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
  }
}

interface IProps {
  compact?: boolean;
}

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");

const { slugInfo, hasContent, fetchContent } = useSlugInfo("Footer");
const { currentCurrency } = useCurrency();
const { footerBlock, chinaDomainIdentifier, isChinaDomain } = useFooterBlock();
const overlayLocales = useOverlayLocales();
const { version } = pkg;
const { openLivePersonChat, startLivePerson } = useLivePerson();
const appVersion = computed(() => version);

watch(slugInfo, async () => {
  if (hasContent.value) {
    await fetchContent();
  }
});
const currencyCode = currentCurrency.value.code;

const openCookieSettings = () => {
  const tryOpen = () => {
    if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === "function") {
      window.OneTrust.ToggleInfoDisplay();
    } else {
      setTimeout(tryOpen, 300);
    }
  };
  tryOpen();
};

const chatLauncher = ref<HTMLElement>();
const expandedChatButton = ref<HTMLButtonElement>();
const restoreChatButton = ref<HTMLButtonElement>();
const chatLauncherModes = ["available-mode", "busy-mode", "offline-mode"] as const;
const chatLauncherMode = ref<(typeof chatLauncherModes)[number]>("available-mode");
const isChatLauncherReady = ref(false);
let chatReadyTimer: ReturnType<typeof setTimeout> | undefined;
let chatModeObserver: MutationObserver | undefined;

const checkChatLauncherReady = () => {
  clearTimeout(chatReadyTimer);
  if (isChatLauncherReady.value || !showChatFooter.value || isChatWindowActive.value || !agentsAvailable.value) {
    return;
  }
  const launcher = chatLauncher.value;
  const provider = launcher?.querySelector('#LP_DIV_1750955724927 [data-lp-event="click"]');
  if (!provider?.querySelector(".vc-icon-with-label__text")?.textContent?.trim() || !provider.querySelector("svg")) {
    return;
  }
  const icons = Array.from(provider.querySelectorAll<SVGSVGElement>("svg"));
  isChatLauncherReady.value = icons.every((icon) => {
    try {
      const bounds = icon.getBBox();
      return bounds.width > 0 && bounds.height > 0;
    } catch {
      return false;
    }
  });
  if (!isChatLauncherReady.value) {
    chatReadyTimer = setTimeout(checkChatLauncherReady, 100);
  }
};

const syncChatLauncherMode = () => {
  const container = chatLauncher.value?.querySelector("#LP_DIV_1750955724927");
  const launcher = container?.querySelector('[data-lp-event="click"]') ?? container;
  const modeIcon = launcher?.querySelector(".available-mode, .busy-mode, .offline-mode");
  chatLauncherMode.value = chatLauncherModes.find((mode) => modeIcon?.classList.contains(mode)) ?? "available-mode";
  checkChatLauncherReady();
};

const isScrollingUp = ref(false);
const isAtBottom = ref(false);
const isBubbleVisible = ref(false);
let lastScrollPosition = 0;

const checkScrollDirectionAndPosition = () => {
  const currentScrollPosition = window.scrollY || document.documentElement.scrollTop;
  const scrollHeight = document.documentElement.scrollHeight;
  const windowHeight = window.innerHeight;

  isAtBottom.value = Math.ceil(currentScrollPosition + windowHeight + 20) >= scrollHeight;
  isScrollingUp.value = currentScrollPosition < lastScrollPosition;
  isBubbleVisible.value = !isAtBottom.value;
  lastScrollPosition = currentScrollPosition;
};

const isChatMinimized = ref(false);
watch(isMobile, (mobile) => {
  if (mobile) {
    isChatMinimized.value = false;
  }
});
const chatPosition = ref("chat-launcher--bottom-right");
let chatPositionFrame = 0;
let chatContentObserver: MutationObserver | undefined;
let chatSizeObserver: ResizeObserver | undefined;

const updateChatPosition = () => {
  chatPositionFrame = 0;
  if (!isChatMinimized.value) {
    chatPosition.value = "chat-launcher--bottom-right";
    return;
  }
  const launcher = chatLauncher.value;
  if (!launcher || !launcher.getClientRects().length) {
    return;
  }
  const { width, height } = launcher.getBoundingClientRect();
  const controls = Array.from(
    document.querySelectorAll(".add-to-cart__button, .add-to-cart__btn-small, .add-to-cart input, .vc-quantity"),
  )
    .map((control) => control.getBoundingClientRect())
    .filter((bounds) => bounds.width && bounds.height && bounds.bottom > 0 && bounds.top < window.innerHeight);
  const rightOffset = isChatMinimized.value ? 0 : 20;
  const candidates = [
    { name: "bottom-right", left: window.innerWidth - width - rightOffset, top: window.innerHeight - height - 40 },
    { name: "top-right", left: window.innerWidth - width - rightOffset, top: 120 },
  ];
  const clearPosition = candidates.find(({ left, top }) =>
    controls.every(
      (bounds) =>
        left + width + 12 <= bounds.left ||
        left - 12 >= bounds.right ||
        top + height + 12 <= bounds.top ||
        top - 12 >= bounds.bottom,
    ),
  );
  chatPosition.value = `chat-launcher--${clearPosition?.name ?? "top-right"}`;
};

const scheduleChatPosition = () => {
  if (!chatPositionFrame) {
    chatPositionFrame = window.requestAnimationFrame(updateChatPosition);
  }
};

const toggleChatLauncher = async () => {
  isChatMinimized.value = !isChatMinimized.value;
  await nextTick();
  const expandedLauncher =
    chatLauncher.value?.querySelector<HTMLElement>('#LP_DIV_1750955724927 [data-lp-event="click"]') ??
    expandedChatButton.value;
  (isChatMinimized.value ? restoreChatButton.value : expandedLauncher)?.focus();
  scheduleChatPosition();
};

watch([isChatMinimized, showChatFooter, isChatWindowActive, agentsAvailable], scheduleChatPosition, { flush: "post" });
watch([showChatFooter, isChatWindowActive, agentsAvailable], checkChatLauncherReady, { flush: "post" });

// Two-letter codes of the overlay locales (German, French, …) the current page has a variant for;
// passed to VcLanguageSelector so it can list/auto-select them.
const pageOverlayLocales = ref<string[]>([]);

onMounted(() => {
  window.addEventListener("scroll", checkScrollDirectionAndPosition, false);
  window.addEventListener("scroll", scheduleChatPosition, { passive: true, capture: true });
  window.addEventListener("resize", scheduleChatPosition);
  chatContentObserver = new MutationObserver(scheduleChatPosition);
  chatContentObserver.observe(document.querySelector("main") ?? document.body, { childList: true, subtree: true });
  chatSizeObserver = new ResizeObserver(scheduleChatPosition);
  if (chatLauncher.value) {
    chatSizeObserver.observe(chatLauncher.value);
  }
  const chatContainer = chatLauncher.value?.querySelector("#LP_DIV_1750955724927");
  if (chatContainer) {
    chatModeObserver = new MutationObserver(syncChatLauncherMode);
    chatModeObserver.observe(chatContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  }
  syncChatLauncherMode();
  scheduleChatPosition();
  eventBus.on("contentLoaded", (pg: IPage) => {
    pageOverlayLocales.value = overlayLocales
      .filter((engine) => engine.hasTranslation(pg))
      .map((engine) => engine.locale);
  });
  initChatWindowObserver();
  initCoBrowseDetection();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", checkScrollDirectionAndPosition, false);
  window.removeEventListener("scroll", scheduleChatPosition, true);
  window.removeEventListener("resize", scheduleChatPosition);
  window.cancelAnimationFrame(chatPositionFrame);
  chatContentObserver?.disconnect();
  chatSizeObserver?.disconnect();
  chatModeObserver?.disconnect();
  clearTimeout(chatReadyTimer);
  eventBus.off("contentLoaded");
  cleanupChatWindowObserver();
  cleanupCoBrowseDetection();
  stopAgentAvailabilityPolling();
});

watch(
  showChatFooter,
  () => {
    startLivePerson(showChatFooter.value);
    // Only poll agent availability while the chat footer is actually shown, so chat
    // disabled for the current language/page never generates background traffic.
    if (showChatFooter.value) {
      startAgentAvailabilityPolling();
    } else {
      stopAgentAvailabilityPolling();
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">
#trustedsite-tm-image {
  @apply relative mx-auto my-2.5 bottom-0 #{!important};
}

.chat-launcher {
  @apply fixed z-50 flex flex-col items-end gap-1 print:hidden;

  &--loading {
    @apply invisible pointer-events-none;
  }

  &--bottom-right {
    @apply bottom-10 right-5;
  }

  &--top-right {
    @apply top-[120px] right-5;
  }

  &--minimized {
    @apply right-0;
  }

  &__minimize,
  &__restore,
  &__open {
    @apply flex items-center justify-center text-[--color-chat] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[--color-chat];
  }

  &__minimize {
    @apply size-8 rounded-full absolute top-[-7px] right-[-7px] z-10 md:top-auto md:right-auto md:bottom-0 md:left-1;
  }

  &__minimize-icon {
    @apply flex size-6 items-center justify-center rounded-full border border-neutral-200 bg-white shadow-sm;
    svg {
      @apply stroke-[--color-neutral-a1];
    }
  }

  &__minimize:hover &__minimize-icon {
    @apply bg-neutral-50;
  }

  &__sidebar {
    @apply w-11 flex-col gap-3 rounded-l-lg  shadow-[0_8px_22px_rgba(0,0,0,0.3)] bg-white py-4 border border-transparent hover:border hover:border-neutral-200 ease-in-out duration-200;
    @apply flex;
  }

  &__restore {
    @apply size-5 self-center;
  }

  &__open {
    @apply w-full flex-col gap-3;
  }

  &__restore-label {
    @apply rotate-180 whitespace-nowrap text-sm font-bold leading-5 [writing-mode:vertical-rl] text-[--color-neutral-a1];
  }
}

.bubble-container,
.chat-launcher__open {
  .available-mode {
    @apply text-[--color-chat];
  }

  .busy-mode {
    @apply text-[--color-primary-a1];
  }

  .offline-mode {
    @apply text-[--color-neutral-a1];
  }
}

.bubble-container {
  &.slideDown,
  &.slideUp {
    .available-mode,
    .busy-mode,
    .offline-mode,
    .chat-widget__icon svg {
      @apply bottom-5;
      transition: bottom 0.3s ease-in-out;
    }
  }

  &.stay .available-mode,
  &.stay .busy-mode,
  &.stay .offline-mode,
  &.stay .chat-widget__icon svg {
    @apply bottom-10;
    transition: none;
  }

  :not(.chat-widget__icon) {
    > .available-mode {
      @apply shrink-0 size-[42px] cursor-pointer;
    }

    > .busy-mode {
      @apply shrink-0 size-[42px] cursor-pointer;
    }

    > .offline-mode {
      @apply shrink-0 size-[42px] cursor-pointer;
    }
  }

  .chat-widget__icon {
    @apply text-[--color-neutral-a1] shrink-0 cursor-pointer gap-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[--color-chat];

    svg {
      @apply size-[52px] md:size-[60px];
    }

    .vc-icon-with-label__text {
      @apply text-center bg-white text-sm font-bold leading-6 pl-10 pr-3 py-1 rounded-[22px] shadow-[0_8px_22px_rgba(0,0,0,0.16)] whitespace-nowrap mt-[-10px] hidden md:flex;
    }
  }

  .vc-icon-with-label--end {
    @apply bg-white shadow-[0_8px_22px_rgba(0,0,0,0.16)] px-2 py-1 rounded-full gap-1;

    .vc-icon-with-label__text {
      @apply bg-transparent shadow-none p-0 mt-0 text-base;
    }
  }
}
</style>
